import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import {
  Mail,
  Copy,
  Check,
  X,
  UserPlus,
  RefreshCw,
  RotateCcw,
  AlertTriangle,
} from "lucide-react";
import { API_URL } from "../../api/api";
import { todayStr } from "../../utils/localDate";
import { T } from "../../components/em/tokens";
import {
  ModalOverlay,
  Card,
  PrimaryButton,
  SecondaryButton,
  Label,
  Input,
  Select,
} from "../../components/em/ui";

const auth = () => {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const labelStyle = {
  font: `600 11px ${T.font}`,
  color: T.fg5,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};

// Screens: 'form' | 'confirm_restore' | 'success'
const AddTeamModal = ({ onClose, onCreated }) => {
  const [screen, setScreen] = useState("form");
  const [inviteLink, setInviteLink] = useState(null);
  const [createdId, setCreatedId] = useState(null);
  const [createdEmpId, setCreatedEmpId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  // One error map for the whole form, keyed by field. Messages always name the
  // field as it is labelled on screen.
  const [errors, setErrors] = useState({});
  const [copied, setCopied] = useState(false);
  // Invitation email state: idle → sending → sent | error.
  const [sendState, setSendState] = useState({ status: "idle" });
  const [deletedMember, setDeletedMember] = useState(null); // { name, designation }
  const [dupes, setDupes] = useState([]); // likely-duplicate matches
  const [dupeAck, setDupeAck] = useState(false); // user chose to create anyway

  // Dropdown option sources (mirrors the profile edit page).
  const [departments, setDepartments] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [managers, setManagers] = useState([]);
  const [nextEmpId, setNextEmpId] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    jobtitle: "",
    department: "",
    manager: "",
    work_shift: "",
    doj: "",
    employee_status: "",
  });

  // Editing a field clears its error — the message goes as soon as it is stale.
  const setField = (k, v) => {
    setFormData((p) => ({ ...p, [k]: v }));
    setErrors((p) => (p[k] ? { ...p, [k]: undefined } : p));
  };

  // ── Load dropdown options + next employee id ───────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      const results = await Promise.allSettled([
        axios.get(`${API_URL}/api/settings/departments`, auth()),
        axios.get(`${API_URL}/api/settings/job-titles`, auth()),
        axios.get(`${API_URL}/api/settings/employment-statuses`, auth()),
        axios.get(`${API_URL}/api/settings/shifts`, auth()),
        axios.get(`${API_URL}/api/org-chart/employees`, auth()),
        axios.get(`${API_URL}/api/team/employees/next-id`, auth()),
      ]);
      if (!alive) return;
      const val = (i) =>
        results[i].status === "fulfilled" ? results[i].value.data : null;
      const arr = (d) =>
        Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];
      setDepartments(arr(val(0)));
      setJobTitles(arr(val(1)));
      setStatuses(arr(val(2)));
      setShifts(arr(val(3)));
      setManagers(
        Array.isArray(val(4)?.employees) ? val(4).employees : arr(val(4)),
      );
      setNextEmpId(val(5)?.next || "");
    })();
    return () => {
      alive = false;
    };
  }, []);

  const generatePassword = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$!";
    let pass = "";
    for (let i = 0; i < 10; i++)
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    setField("password", pass);
  };

  // Names resolved from the chosen IDs — kept readable for the profile page.
  const selectedJobTitle = useMemo(
    () =>
      jobTitles.find((j) => String(j.id) === String(formData.jobtitle)) || null,
    [jobTitles, formData.jobtitle],
  );
  const selectedManager = useMemo(
    () =>
      managers.find((m) => String(m.id) === String(formData.manager)) || null,
    [managers, formData.manager],
  );

  // Mandatory fields, labelled exactly as the form labels them so an error
  // never mentions a database column.
  const REQUIRED_FIELDS = [
    ["name", "Full name"],
    ["email", "Email"],
    // Password is optional: left blank, the employee gets an invite link and
    // chooses their own. Filled in, the account is active immediately.
    ["jobtitle", "Job title"],
    ["manager", "Manager"],
    ["work_shift", "Work shift"],
    ["employee_status", "Status"],
    ["doj", "Date of joining"],
  ];

  const validate = () => {
    const errs = {};
    REQUIRED_FIELDS.forEach(([field, label]) => {
      if (!String(formData[field] ?? "").trim()) errs[field] = `${label} is required`;
    });
    if (!errs.email && !/^\S+@\S+\.\S+$/.test(formData.email))
      errs.email = "Enter a valid email";
    if (!errs.doj && formData.doj > todayStr())
      errs.doj = "Joining date can't be in the future";
    return errs;
  };

  // Inline error text under a field.
  const fieldError = (name) =>
    errors[name] ? (
      <span
        style={{
          font: `400 11.5px ${T.font}`,
          color: T.danger,
          marginTop: 4,
          display: "block",
        }}
        role="alert"
      >
        {errors[name]}
      </span>
    ) : null;

  // Always writes the same property. Previously this returned a bare
  // `borderColor` on error and {} otherwise — and because Input/Select set the
  // `border` shorthand in their own base style, React removed the longhand when
  // the error cleared and the border's colour went with it, leaving the field
  // looking borderless once you picked a value.
  const invalidStyle = (name) => ({
    border: `1px solid ${errors[name] ? T.danger : T.border}`,
  });

  // The payload shared by create + restore.
  const buildPayload = () => ({
    name: formData.name,
    email: formData.email,
    password: formData.password,
    // Store the readable job-title name in `designation` so profiles show it,
    // and the FK ids for the edit-page dropdowns.
    designation: selectedJobTitle?.name || "",
    jobtitle: formData.jobtitle || null,
    department: formData.department || null,
    work_shift: formData.work_shift || null,
    doj: formData.doj || null,
    employee_status: formData.employee_status || null,
    reporting_manager: selectedManager?.name || null,
  });

  // Assign the org-chart primary manager for a freshly created employee.
  const assignManager = async (memberId) => {
    if (!memberId || !formData.manager) return;
    try {
      await axios.post(
        `${API_URL}/api/org-chart/assign-manager`,
        {
          employee_id: Number(memberId),
          primary_manager_id: Number(formData.manager),
          dotted_manager_id: null,
        },
        auth(),
      );
    } catch (e) {
      // Non-fatal: the employee is created; manager link can be set later.
      console.warn(
        "Manager assignment failed:",
        e?.response?.data?.error || e.message,
      );
    }
  };

  // Actually create the employee (shared by the normal submit and the
  // "Create anyway" override, so neither depends on async state timing).
  const doCreate = async () => {
    setErrors({});
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/team/employees/create`,
        buildPayload(),
        auth(),
      );
      await assignManager(res.data?.member_id || res.data?.id);
      await showSuccess(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.clear();
        window.location.assign("/login");
        return;
      }
      const data = err?.response?.data || {};
      if (data.code === "EMAIL_WAS_DELETED") {
        setDeletedMember(data.member);
        setScreen("confirm_restore");
      } else if (/email/i.test(data.error || "")) {
        setErrors({ email: data.error });
      } else {
        alert(data.error || "Failed to create employee");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Create new employee ────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Catch every empty mandatory field at once, before the server does — and
    // before the duplicate check spends a round trip.
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    // Non-AI duplicate guard: warn on a likely existing record before creating.
    if (!dupeAck) {
      try {
        const [first_name, ...rest] = (formData.name || "").trim().split(/\s+/);
        const { data: dup } = await axios.post(
          `${API_URL}/api/team/duplicates`,
          { first_name, last_name: rest.join(" "), email: formData.email },
          auth(),
        );
        if (Array.isArray(dup?.matches) && dup.matches.length) {
          setDupes(dup.matches);
          return; // hold — show the warning; the button becomes "Create anyway"
        }
      } catch {
        /* duplicate check is best-effort — never blocks creation */
      }
    }
    await doCreate();
  };

  // ── Restore previously deleted employee ─────────────────────────────────────
  const handleRestore = async () => {
    setRestoring(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/team/employees/restore`,
        buildPayload(),
        auth(),
      );
      await assignManager(res.data?.member_id || res.data?.id);
      await showSuccess(res.data);
    } catch (err) {
      alert(err?.response?.data?.error || "Restore failed. Please try again.");
    } finally {
      setRestoring(false);
    }
  };

  // ── Shared success handler ───────────────────────────────────────────────
  const showSuccess = async (data) => {
    // Nothing is copied or emailed automatically — HR chooses both explicitly
    // from the success dialog.
    setInviteLink(
      data.invite_link || `${window.location.origin}/onboarding/${data.token}`,
    );
    setCreatedId(data.member_id || data.id || null);
    setCreatedEmpId(data.employeeid || nextEmpId || null);
    setSendState({ status: "idle" });
    setScreen("success");
  };

  // Sends the invitation on demand. The server re-mints the token as it sends,
  // so we swap in the link it returns — the previously shown one is now dead.
  const sendInvite = async () => {
    if (!createdId) return;
    setSendState({ status: "sending" });
    try {
      const res = await axios.post(
        `${API_URL}/api/team/employees/${createdId}/invite`,
        {},
        auth(),
      );
      if (res.data?.link) {
        setInviteLink(res.data.link);
        setCopied(false); // the copy button now refers to a different link
      }
      setSendState({ status: "sent" });
    } catch (err) {
      const data = err?.response?.data;
      setSendState({
        status: "error",
        msg:
          (typeof data?.error === "string" ? data.error : data?.error?.message) ||
          "Could not send the invitation.",
      });
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const handleDone = () => {
    if (onCreated)
      onCreated({ id: createdId, employeeid: createdEmpId, ...buildPayload() });
    onClose?.();
  };

  const iconWrap = (Icon, tone = T.orange, bg = T.orange50) => (
    <span
      style={{
        width: 38,
        height: 38,
        borderRadius: 11,
        background: bg,
        color: tone,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon size={18} />
    </span>
  );

  const Header = ({ icon, title, subtitle, tone, bg, onX }) => (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        marginBottom: 18,
      }}
    >
      {iconWrap(icon, tone, bg)}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            font: `700 17px ${T.font}`,
            color: T.fg1,
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </div>
        <div
          style={{ font: `400 12.5px ${T.font}`, color: T.fg5, marginTop: 2 }}
        >
          {subtitle}
        </div>
      </div>
      <button
        type="button"
        onClick={onX}
        aria-label="Close"
        style={{
          background: "none",
          border: 0,
          cursor: "pointer",
          color: T.fg5,
          padding: 4,
          lineHeight: 0,
        }}
      >
        <X size={18} />
      </button>
    </div>
  );

  const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 };

  return (
    <ModalOverlay onClose={onClose}>
      <Card
        onClick={(e) => e.stopPropagation()}
        style={{
          width: screen === "form" ? 620 : 480,
          maxWidth: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          padding: 24,
        }}
      >
        {/* ══ SCREEN 1: Add Employee Form ══════════════════════════════════ */}
        {screen === "form" && (
          <>
            <Header
              icon={UserPlus}
              title="Add New Employee"
              subtitle="Create an account and generate an onboarding link."
              tone={T.orange}
              bg={T.orange50}
              onX={onClose}
            />

            <form
              onSubmit={handleSubmit}
              style={{ display: "flex", flexDirection: "column", gap: 16 }}
            >
              {/* Account */}
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                <div style={labelStyle}>Account</div>
                <div style={grid2}>
                  <div>
                    <Label>
                      Full Name <span style={{ color: T.danger }}>*</span>
                    </Label>
                    <Input
                      required
                      value={formData.name}
                      onChange={(e) => setField("name", e.target.value)}
                      placeholder="e.g. Priya Sharma"
                      aria-invalid={!!errors.name}
                      style={invalidStyle("name")}
                    />
                    {fieldError("name")}
                  </div>
                  <div>
                    <Label>Employee ID</Label>
                    <Input
                      value={nextEmpId}
                      readOnly
                      disabled
                      placeholder="Auto-generated"
                      style={{
                        background: T.bgHead,
                        color: T.fg5,
                        cursor: "not-allowed",
                      }}
                    />
                  </div>
                  <div>
                    <Label>
                      Email <span style={{ color: T.danger }}>*</span>
                    </Label>
                    <Input
                      required
                      type="email"
                      value={formData.email}
                      onChange={(e) => setField("email", e.target.value)}
                      aria-invalid={!!errors.email}
                      placeholder="name@company.com"
                      style={invalidStyle("email")}
                    />
                    {fieldError("email")}
                  </div>
                  <div>
                    <Label>
                      Password{" "}
                      <span style={{ color: T.fg7, fontWeight: 400 }}>(optional)</span>
                    </Label>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Input
                        value={formData.password}
                        onChange={(e) => setField("password", e.target.value)}
                        placeholder="Leave blank to email an invite link"
                      />
                      <button
                        type="button"
                        onClick={generatePassword}
                        style={{
                          flexShrink: 0,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          padding: "0 12px",
                          borderRadius: T.rMd,
                          border: `1px solid ${T.border}`,
                          background: T.bgHead,
                          color: T.fg3,
                          font: `500 12px ${T.font}`,
                          cursor: "pointer",
                        }}
                      >
                        <RefreshCw size={13} /> Generate
                      </button>
                    </div>
                    {fieldError("password")}
                  </div>
                </div>
                <small style={{ font: `400 11.5px ${T.font}`, color: T.fg7 }}>
                  Leave the password blank and the employee is emailed a link to
                  set their own — nothing is sent in plain text. Fill it in and
                  the account is usable straight away.
                </small>
              </div>

              {/* Role & placement */}
              <div
                style={{
                  borderTop: `1px solid ${T.divider}`,
                  paddingTop: 14,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <div style={labelStyle}>
                  Role &amp; placement{" "}
                  <span
                    style={{
                      textTransform: "none",
                      letterSpacing: 0,
                      color: T.fg7,
                      fontWeight: 400,
                    }}
                  >
                    · department is optional; the rest are required
                  </span>
                </div>
                <div style={grid2}>
                  <div>
                    <Label>
                      Designation / Job Title{" "}
                      <span style={{ color: T.danger }}>*</span>
                    </Label>
                    <Select
                      value={formData.jobtitle}
                      onChange={(e) => setField("jobtitle", e.target.value)}
                      aria-invalid={!!errors.jobtitle}
                      style={invalidStyle("jobtitle")}
                    >
                      <option value="">Select job title…</option>
                      {jobTitles.map((j) => (
                        <option key={j.id} value={j.id}>
                          {j.name}
                        </option>
                      ))}
                    </Select>
                    {fieldError("jobtitle")}
                  </div>
                  <div>
                    <Label>Department</Label>
                    <Select
                      value={formData.department}
                      onChange={(e) => setField("department", e.target.value)}
                    >
                      <option value="">Select department…</option>
                      {departments.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <Label>
                      Manager <span style={{ color: T.danger }}>*</span>
                    </Label>
                    <Select
                      value={formData.manager}
                      onChange={(e) => setField("manager", e.target.value)}
                      aria-invalid={!!errors.manager}
                      style={invalidStyle("manager")}
                    >
                      <option value="">Select manager…</option>
                      {managers.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                          {m.designation ? ` · ${m.designation}` : ""}
                        </option>
                      ))}
                    </Select>
                    {fieldError("manager")}
                  </div>
                  <div>
                    <Label>
                      Work Shift <span style={{ color: T.danger }}>*</span>
                    </Label>
                    <Select
                      value={formData.work_shift}
                      onChange={(e) => setField("work_shift", e.target.value)}
                      aria-invalid={!!errors.work_shift}
                      style={invalidStyle("work_shift")}
                    >
                      <option value="">Select shift…</option>
                      {shifts.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </Select>
                    {fieldError("work_shift")}
                  </div>
                  <div>
                    <Label>
                      Date of Joining <span style={{ color: T.danger }}>*</span>
                    </Label>
                    <Input
                      type="date"
                      max={todayStr()}
                      value={formData.doj}
                      onChange={(e) => setField("doj", e.target.value)}
                      aria-invalid={!!errors.doj}
                      style={invalidStyle("doj")}
                    />
                    {fieldError("doj")}
                  </div>
                  <div>
                    <Label>
                      Status <span style={{ color: T.danger }}>*</span>
                    </Label>
                    <Select
                      value={formData.employee_status}
                      onChange={(e) =>
                        setField("employee_status", e.target.value)
                      }
                      aria-invalid={!!errors.employee_status}
                      style={invalidStyle("employee_status")}
                    >
                      <option value="">Select status…</option>
                      {statuses.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </Select>
                    {fieldError("employee_status")}
                  </div>
                </div>
              </div>

              {dupes.length > 0 ? (
                <div
                  role="alert"
                  style={{
                    background: "rgba(234,179,8,0.10)",
                    border: `1px solid ${T.warning || "#eab308"}`,
                    borderRadius: T.rMd,
                    padding: "12px 14px",
                    font: `400 12.5px/1.6 ${T.font}`,
                    color: T.fg2,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 7, font: `700 12.5px ${T.font}`, color: T.fg1, marginBottom: 6 }}>
                    <AlertTriangle size={15} color={T.warning || "#eab308"} />
                    Possible duplicate{dupes.length > 1 ? "s" : ""} found
                  </div>
                  {dupes.slice(0, 4).map((d) => (
                    <div key={d.id} style={{ marginTop: 2 }}>
                      • <b>{[d.first_name, d.last_name].filter(Boolean).join(" ")}</b>
                      {d.employeeid ? ` (${d.employeeid})` : ""} — {d.email}
                      <span style={{ color: T.fg7 }}> · matches {d.reasons?.join(", ")}</span>
                    </div>
                  ))}
                  <div style={{ marginTop: 8, color: T.fg5 }}>
                    Review before continuing, or create anyway if this is a genuinely new person.
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    background: T.orange50,
                    border: `1px solid ${T.orangeEdge || T.border}`,
                    borderRadius: T.rMd,
                    padding: "10px 14px",
                    font: `400 12px/1.6 ${T.font}`,
                    color: T.fg3,
                  }}
                >
                  After creating the employee, an onboarding link appears so you
                  can share it via email or copy it.
                </div>
              )}

              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}
              >
                <SecondaryButton type="button" onClick={onClose}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  type={dupes.length > 0 ? "button" : "submit"}
                  icon={submitting ? RefreshCw : UserPlus}
                  disabled={submitting}
                  onClick={dupes.length > 0 ? () => { setDupeAck(true); doCreate(); } : undefined}
                >
                  {submitting
                    ? "Creating…"
                    : dupes.length > 0
                      ? "Create anyway"
                      : "Create Employee"}
                </PrimaryButton>
              </div>
            </form>
          </>
        )}

        {/* ══ SCREEN 2: Restore Confirmation ═══════════════════════════════ */}
        {screen === "confirm_restore" && (
          <>
            <Header
              icon={AlertTriangle}
              title="Previously Removed Employee"
              subtitle="This email belongs to a former team member."
              tone={T.orange}
              bg={T.orange50}
              onX={onClose}
            />

            <div
              style={{
                background: T.orange50,
                border: `1px solid ${T.orangeEdge || T.border}`,
                borderRadius: 10,
                padding: "14px 16px",
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  font: `600 13px ${T.font}`,
                  color: T.fg1,
                  marginBottom: 4,
                }}
              >
                Previous record found
              </div>
              <div style={{ font: `400 13px/1.6 ${T.font}`, color: T.fg3 }}>
                <b>{deletedMember?.name || formData.email}</b>
                {deletedMember?.designation
                  ? ` · ${deletedMember.designation}`
                  : ""}{" "}
                was previously removed from the team.
              </div>
            </div>

            <p
              style={{
                font: `400 13px/1.6 ${T.font}`,
                color: T.fg3,
                marginBottom: 18,
              }}
            >
              Restore their account? Historical data (joining date, documents,
              attendance) is preserved. Their password is reset and a fresh
              onboarding link is generated.
            </p>

            <div
              style={{
                background: T.bgHead,
                border: `1px solid ${T.border}`,
                borderRadius: 8,
                padding: "12px 16px",
                marginBottom: 22,
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  gap: "6px 16px",
                  font: `400 13px ${T.font}`,
                  color: T.fg3,
                }}
              >
                <span style={{ color: T.fg5 }}>Name</span>
                <span>
                  <b>{formData.name}</b>
                </span>
                <span style={{ color: T.fg5 }}>Job Title</span>
                <span>
                  <b>{selectedJobTitle?.name || "—"}</b>
                </span>
                <span style={{ color: T.fg5 }}>Email</span>
                <span>
                  <b>{formData.email}</b>
                </span>
              </div>
            </div>

            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}
            >
              <SecondaryButton
                type="button"
                onClick={() => {
                  setScreen("form");
                  setDeletedMember(null);
                }}
              >
                Back
              </SecondaryButton>
              <PrimaryButton
                icon={restoring ? RefreshCw : RotateCcw}
                onClick={handleRestore}
                disabled={restoring}
              >
                {restoring ? "Restoring…" : "Yes, Restore"}
              </PrimaryButton>
            </div>
          </>
        )}

        {/* ══ SCREEN 3: Success ════════════════════════════════════════════ */}
        {screen === "success" && (
          <>
            <Header
              icon={Check}
              title={deletedMember ? "Employee Restored" : "Employee Created"}
              subtitle="Share the onboarding link with the employee."
              tone={T.success || "#16a34a"}
              bg="rgba(22,163,74,0.10)"
              onX={handleDone}
            />

            <div
              style={{
                background: T.bgHead,
                border: `1px solid ${T.border}`,
                borderRadius: 10,
                padding: "14px 16px",
                marginBottom: 18,
              }}
            >
              <div style={{ font: `400 13px/1.6 ${T.font}`, color: T.fg3 }}>
                <b>{formData.name}</b> has been{" "}
                {deletedMember ? "restored to" : "added to"} your team.
              </div>
              <div
                style={{
                  font: `400 12px ${T.font}`,
                  color: T.fg5,
                  marginTop: 2,
                }}
              >
                {formData.email}
                {selectedJobTitle?.name ? ` · ${selectedJobTitle.name}` : ""}
                {createdEmpId ? ` · ${createdEmpId}` : ""}
              </div>
            </div>

            <Label>Onboarding Link</Label>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <Input value={inviteLink} readOnly />
              <button
                type="button"
                onClick={copyLink}
                aria-label="Copy link"
                title={copied ? "Copied!" : "Copy link"}
                style={{
                  flexShrink: 0,
                  width: 40,
                  borderRadius: T.rMd,
                  border: `1px solid ${T.border}`,
                  background: T.bgHead,
                  color: copied ? T.success || "#16a34a" : T.fg3,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                }}
              >
                {copied ? <Check size={15} /> : <Copy size={15} />}
              </button>
            </div>
            <div
              style={{
                font: `400 11.5px ${T.font}`,
                color: T.fg7,
                marginBottom: sendState.status === "idle" ? 20 : 8,
              }}
            >
              This link expires in 48 hours.
            </div>

            {sendState.status === "sent" && (
              <div
                style={{
                  font: `500 12px ${T.font}`,
                  color: T.success600,
                  marginBottom: 20,
                }}
              >
                Invitation sent to {formData.email}. The link above has been
                refreshed to match.
              </div>
            )}
            {sendState.status === "error" && (
              <div
                style={{
                  font: `500 12px ${T.font}`,
                  color: T.danger,
                  marginBottom: 20,
                }}
              >
                {sendState.msg}
              </div>
            )}

            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}
            >
              <SecondaryButton
                type="button"
                onClick={sendInvite}
                disabled={sendState.status === "sending"}
                icon={Mail}
              >
                {sendState.status === "sending"
                  ? "Sending…"
                  : sendState.status === "sent"
                    ? "Resend Email"
                    : "Send via Email"}
              </SecondaryButton>
              <PrimaryButton icon={Check} onClick={handleDone}>
                Done
              </PrimaryButton>
            </div>
          </>
        )}
      </Card>
    </ModalOverlay>
  );
};

AddTeamModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
};

export default AddTeamModal;
